import { logGreyErrorFromAccounts, logUsageFromAccounts } from "owa-account-analytics";
import { StepsCustomData } from "owa-analytics-record-steps";
import { getAccountKeyForMailboxInfo } from "owa-client-types";
import type { CoprincipalAccountSource } from "../store/schema/AccountSourceList";
import tryGetGlobalSettingsAccountMailboxInfo from "../selectors/tryGetGlobalSettingsAccountMailboxInfo";
import { AccountStatus, App, getAccounts } from "./filterAccounts";

// Value used when the account does not match the sourceId
const doesNotMatchSourceId = -1;

/**
 * Returns an array of the indexes that match the supplied sourceId
 * @param sourceId SourceId to be matched to accounts
 * @returns Comma seperate list of the matching account indexes
 */
function accountsMatchingSourceId(sourceId: string): string {
    return getAccounts(AccountStatus.Any, App.All)
        .map((account, index) => (account.sourceId === sourceId ? index : doesNotMatchSourceId))
        .filter(index => index !== doesNotMatchSourceId)
        .map(index => index.toString())
        .join(",");
}

/**
 * Adds additional diagnostic data for the match
 * @param match The matching coprincipal account for which data is to be logged
 * @param first The first coprincipal account that matched the account key
 * @param index Index of the entry in the list of matches
 * @param diagnosticData Diagnostic data to which data for the specified matching coprincipal account
 */
function addDiagnosticDataForMatch(match: CoprincipalAccountSource, matches: CoprincipalAccountSource[], steps: StepsCustomData) {
    const type = match.sourceType.toString();
    const smtpLen = match.mailboxInfo.mailboxSmtpAddress.length;
    const userIdLen = match.mailboxInfo.userIdentity.length;
    const sameSmtp = matches
        .map(item => item.mailboxInfo.mailboxSmtpAddress == match.mailboxInfo.mailboxSmtpAddress ? "Y" : "N")
        .join(",");
    const sameUserId = matches
        .map(item => (item.mailboxInfo.userIdentity == match.mailboxInfo.userIdentity ? "Y" : "N"))
        .join(",");
    const samePersistenceId = matches
        .map(item => (item.persistenceId == match.persistenceId ? "Y" : "N"))
        .join(",");
    const sameType = matches
        .map(item => (item.sourceType == match.sourceType ? "Y" : "N"))
        .join(",");
    const sourceIdMatches = accountsMatchingSourceId(match.sourceId);
    
    steps.addStep("match", {
        type,
        smtpLen,
        userIdLen,
        sameSmtp,
        sameUserId,
        samePersistenceId,
        sameType,
        sourceIdMatches,
    });
}

/**
 * Logs information about the duplicate matches that were found for the account key
 * and returns an error that with this diagnostic data
 * @param accountKey The account key that was matched
 * @param matches Array of matches for an account key
 */
function logDuplicateAccountKeyFound(accountKey: string, matches: CoprincipalAccountSource[]) {
    const steps = new StepsCustomData();
    steps.addStep("matches", { cnt: matches.length, indexerLen: accountKey.length });
    
    for (const match of matches) {
        addDiagnosticDataForMatch(match, matches, steps);
    }
    
    const diagnosticData = {
        ...steps.getCustomDataMapForSteps(),
        persistenceAccountKey: true,
    };
    
    const err = new Error("DuplicateCoprincipalIndexerFound");
    
    logGreyErrorFromAccounts("DuplicateCoprincipalIndexerFound", matches[0].mailboxInfo, err, diagnosticData);
    return err;
}

const loggedAccountKeys = new Set<string>();

function logAccountNotFoundForAccountKey(accountKey: string) {
    // We only want to log once per account per session
    if (!loggedAccountKeys.has(accountKey)) {
        // Use tryGet to avoid throwing when the store is not yet initialized
        // (zero-account or early-boot states where missing match is expected).
        const mailboxInfo = tryGetGlobalSettingsAccountMailboxInfo(false);
        if (!mailboxInfo) {
            return;
        }
        
        loggedAccountKeys.add(accountKey);
        
        const err = new Error("AccountNotFoundForIndexer");
        
        logUsageFromAccounts("AccountNotFoundForIndexer", mailboxInfo, {
            stack: err.stack,
        });
    }
}

/**
 * Returns the CoprincipalAccountSource associated with the specified account key
 * @param accountKey Value obtained via a getAccountKeyForMailboxInfo call
 * @returns CoprincipalAccountSource if found or undefined if not found
 */
export default function getCoprincipalAccountForAccountKey(accountKey: string, logIfNotFound: boolean = false): CoprincipalAccountSource | undefined {
    const matches = getAccounts(AccountStatus.Any, App.All).filter(account => getAccountKeyForMailboxInfo(account.mailboxInfo) == accountKey);
    
    if (logIfNotFound && matches.length === 0) {
        // We expected that we should find the account for an account key but did not
        logAccountNotFoundForAccountKey(accountKey);
    }
    else if (matches.length > 1) {
        // We should not find more than one account key. Log the error details and
        // the raise an alert but do not block the application from continuing
        logDuplicateAccountKeyFound(accountKey, matches);
    }
    
    return matches[0];
}
